<template>
  <div>
    <accounts-loader
      :client-id="clientId"
      :model-value="accounts"
      @update:modelValue="$emit('update:accounts', $event)"
      ref="accounts"
    />
    <platform-switcher
      :platforms="platforms"
      :model-value="platform"
      @loaded="onPlatformsLoaded"
      @update:model-value="$emit('update:platform', $event)"
    >
      <template v-slot:header="scope">
        <q-item-section class="text-body1">
          <div :class="{ 'text-weight-bold': scope.isActive }">
            {{ scope.platform.name }}
          </div>
          <div
            v-if="isActionNeeded(scope.platform.id)"
            class="text-warning text-weight-bold text-uppercase"
          >
            Action Needed
          </div>
        </q-item-section>
      </template>
      <template v-slot="{ isSplit }">
        <div
          v-if="platform"
          class="bg-white full-height"
          :class="{ 'q-pt-md': isSplit }"
          >
          <div v-if="isSplit" class="text-h6 text-center text-weight-regular q-mb-md">
            <site-icon :social-site="platform" class="on-left" />
            {{ platform.name }} Account
          </div>
          <slot v-bind="{ account: getAccount(platform.id) }">
            <account
              v-if="getAccount(platform.id)"
              :account="getAccount(platform.id)"
              class="q-pa-sm"
              @update:account="set"
              @error="$refs.accounts.load()"
            />
            <app-user
              v-else
              v-slot="{ isServiceProvider, isCustomer, isClient }"
              class="q-pa-lg text-center text-body1"
            >
              <import-posts v-if="isClient" :client-id="clientId" :platform="platform" />
              <div v-else>
                No {{ platform.name }} account was added by the client.
                <br>
                <import-posts-btn
                  v-if="isServiceProvider || isCustomer"
                  :platform="platform"
                  :client-id="clientId"
                  color="primary"
                  class="q-mt-sm"
                  rounded
                />
              </div>
            </app-user>
          </slot>
        </div>
      </template>
    </platform-switcher>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Platform, Account as AccountType } from '@/social/types';
import PlatformSwitcher from '@/social/site/PlatformSwitcher.vue';
import recognition from '@/auth/recognition';
import AppUser from '@/auth/AppUser.vue';
import SiteIcon from '@/social/site/SiteIcon.vue';
import ImportPosts from '@/social/post/import/no-account/ImportPosts.vue';
import Account from '../Account.vue';
import AccountsLoader from '../AccountsLoader.vue';
import ImportPostsBtn from '../../post/import/no-account/ImportPostsBtn.vue';

interface Data {
  task: Promise<any>|null
}

export default defineComponent({
  emits: ['update:accounts', 'update:platform', 'update:platforms'],
  components: {
    ImportPostsBtn,
    ImportPosts,
    AppUser,
    SiteIcon,
    PlatformSwitcher,
    AccountsLoader,
    Account,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    platforms: {
      type: Array as () => Platform[],
      default: () => [],
    },
    accounts: {
      type: Array as () => AccountType[],
      default: () => [],
    },
    platform: Object as () => Platform,
  },
  data(): Data {
    return {
      task: null,
    };
  },
  methods: {
    onPlatformsLoaded(platforms: Platform[]): void {
      this.$emit('update:platforms', platforms);
    },
    getAccount(socialSiteId: number): AccountType|undefined {
      return this.accounts.find(sa => sa.socialSite.id === socialSiteId);
    },
    set(account: AccountType): void {
      const index = this.accounts.findIndex(a => a.id === account.id);
      if (index >= 0) {
        const accounts = [...this.accounts];
        accounts.splice(index, 1, account);
        this.$emit('update:accounts', accounts);
      }
    },
    isActionNeeded(platformId: number): boolean {
      const account = this.getAccount(platformId);
      if (!account || !account.reconnectRequest) return false;
      if (recognition.isServiceProvider()) {
        return account.reconnectRequest.assignee === 'support';
      }
      if (recognition.isClient()) {
        return account.reconnectRequest.assignee === 'client';
      }
      return false;
    },
  },
});
</script>
